import React, { useEffect, useState } from 'react';
import type { User, PathwayBlock, ApiVerification } from '../../../types';
import { DataService } from '../../../services/firebase';
import { useTranslation } from 'react-i18next';
import { Globe, CheckCircle, XCircle, Loader2, Info } from 'lucide-react';

interface ApiBlockViewProps {
  user: User;
  block: PathwayBlock;
  pathwayId: string;
}

export const ApiBlockView: React.FC<ApiBlockViewProps> = ({ user, block, pathwayId }) => {
  const { t } = useTranslation();
  const [verification, setVerification] = useState<ApiVerification | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const config = block.apiConfig;

  useEffect(() => {
    const load = async () => {
      try {
        const v = await DataService.getApiVerification(user.id, pathwayId, block.id);
        setVerification(v);

        if (v && (v.status === 'success' || v.status === 'manual_override')) {
          await DataService.setBlockProgress({
            candidateId: user.id,
            pathwayId,
            blockId: block.id,
            status: 'complete',
            completedAt: v.verifiedAt || v.overriddenAt,
          });
        }
      } catch (e) {
        console.error('Failed to load API verification', e);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [user.id, block.id, pathwayId]);

  const status = verification?.status || 'pending';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-aero-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-2xl mx-auto space-y-4">
      {/* Block header */}
      <div className="bg-white rounded-xl border border-aero-200 p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-aero-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Globe className="w-5 h-5 text-aero-600" />
          </div>
          <div>
            <p className="text-xs font-bold text-aero-400 uppercase tracking-wide mb-1">{t('candidate.block.api.title')}</p>
            <h2 className="text-lg font-bold text-aero-900">{block.name}</h2>
            {block.description && <p className="text-sm text-aero-600 mt-1">{block.description}</p>}
          </div>
        </div>
      </div>

      {/* Provider */}
      {config?.provider && (
        <div className="bg-aero-50 rounded-xl border border-aero-200 p-4 flex items-center gap-3">
          <Globe className="w-4 h-4 text-aero-400 flex-shrink-0" />
          <div>
            <p className="text-xs font-semibold text-aero-600">{t('candidate.block.api.provider')}</p>
            <p className="text-sm text-aero-800 font-medium">{config.provider}</p>
          </div>
        </div>
      )}

      {/* Status card */}
      <div className={`rounded-xl border-2 p-5 ${
        status === 'success' || status === 'manual_override' ? 'bg-green-50 border-green-200' :
        status === 'failure' ? 'bg-red-50 border-red-200' :
        'bg-aero-50 border-aero-200'
      }`}>
        {(status === 'success' || status === 'manual_override') ? (
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <div>
              <p className="text-sm font-semibold text-green-700">{t('candidate.block.api.verified')}</p>
              {verification?.verifiedAt && (
                <p className="text-xs text-green-600 mt-0.5">{new Date(verification.verifiedAt).toLocaleDateString()}</p>
              )}
              {status === 'manual_override' && (
                <p className="text-xs text-green-500 mt-0.5">{t('candidate.block.api.manualOverride')}</p>
              )}
            </div>
          </div>
        ) : status === 'failure' ? (
          <div className="flex items-center gap-3">
            <XCircle className="w-5 h-5 text-red-500" />
            <div>
              <p className="text-sm font-semibold text-red-700">{t('candidate.block.api.failed')}</p>
              <p className="text-xs text-red-600 mt-0.5">{t('candidate.block.api.failedDesc')}</p>
              {verification?.errorMessage && (
                <p className="text-xs text-red-500 mt-1 italic">{verification.errorMessage}</p>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <Loader2 className="w-5 h-5 text-aero-400 animate-spin" />
            <div>
              <p className="text-sm font-semibold text-aero-700">{t('candidate.block.api.pending')}</p>
              <p className="text-xs text-aero-500 mt-0.5">{t('candidate.block.api.pendingDesc')}</p>
            </div>
          </div>
        )}
      </div>

      {/* Success criteria */}
      {config?.successCriteria && (
        <div className="bg-aero-50 rounded-xl border border-aero-200 p-4 flex items-start gap-3">
          <Info className="w-4 h-4 text-aero-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs font-semibold text-aero-600">{t('candidate.block.api.criteria')}</p>
            <p className="text-sm text-aero-700 mt-0.5">{config.successCriteria}</p>
          </div>
        </div>
      )}
    </div>
  );
};
